import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calculator, ArrowRight } from 'lucide-react';
import { PositionMappingService } from '@/services/PositionMappingService';
import { toast } from 'sonner';

interface MappingTesterProps {
  className?: string;
}

export function MappingTester({ className }: MappingTesterProps) {
  const [symbol, setSymbol] = useState('');
  const [tvPosition, setTvPosition] = useState('');
  const [result, setResult] = useState<{
    symbol: string;
    tvPosition: number;
    cTraderLots: number;
    ratio: number;
    contractSize: number;
    positionValue: number;
    mapping: any;
  } | null>(null);
  const [testing, setTesting] = useState(false);

  const testMapping = async () => {
    if (!symbol.trim() || !tvPosition.trim()) {
      toast.error('Please enter both symbol and TV position size');
      return;
    }

    const tvPositionNum = parseFloat(tvPosition);
    if (isNaN(tvPositionNum) || tvPositionNum <= 0) {
      toast.error('TV position size must be a positive number');
      return;
    }

    setTesting(true);
    try {
      // Get mapping from database
      const mapping = await PositionMappingService.getMappingBySymbol(symbol.toUpperCase());
      
      if (!mapping) {
        toast.error(`No mapping found for ${symbol.toUpperCase()}`);
        setResult(null);
        return;
      }

      // Calculate conversion
      const cTraderLots = tvPositionNum * mapping.tv_to_ctrader_ratio;
      const positionValue = cTraderLots * mapping.contract_size;

      setResult({
        symbol: mapping.symbol,
        tvPosition: tvPositionNum,
        cTraderLots,
        ratio: mapping.tv_to_ctrader_ratio,
        contractSize: mapping.contract_size,
        positionValue,
        mapping
      });

      toast.success('Mapping test completed successfully');
    } catch (error) {
      console.error('Error testing mapping:', error);
      toast.error('Failed to test mapping');
      setResult(null);
    } finally {
      setTesting(false);
    }
  };

  const clearTest = () => {
    setSymbol('');
    setTvPosition('');
    setResult(null);
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5" />
          Mapping Tester
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Input Form */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="test-symbol">Symbol</Label>
            <Input
              id="test-symbol"
              placeholder="e.g., EURUSD"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              className="uppercase"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="test-position">TV Position Size</Label>
            <Input
              id="test-position"
              type="number"
              step="0.01"
              placeholder="e.g., 100"
              value={tvPosition}
              onChange={(e) => setTvPosition(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button 
            onClick={testMapping} 
            disabled={testing || !symbol.trim() || !tvPosition.trim()}
            className="flex-1"
          >
            {testing ? 'Testing...' : 'Test Mapping'}
          </Button>
          <Button variant="outline" onClick={clearTest}>
            Clear
          </Button>
        </div>

        {/* Results */}
        {result && (
          <div className="border-t pt-4 space-y-4">
            <div className="flex items-center justify-center gap-4 text-lg font-medium">
              <div className="text-center">
                <div className="text-sm text-muted-foreground">TV Position</div>
                <div>{result.tvPosition}</div>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground" />
              <div className="text-center">
                <div className="text-sm text-muted-foreground">cTrader Lots</div>
                <div className="text-primary">{result.cTraderLots.toFixed(6)}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Symbol:</span>
                  <Badge variant="outline">{result.symbol}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Category:</span>
                  <Badge variant="secondary">{result.mapping.category}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Base Unit:</span>
                  <span>{result.mapping.base_unit}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">TV → CT Ratio:</span>
                  <span className="font-medium">{result.ratio}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contract Size:</span>
                  <span className="font-medium">{result.contractSize.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Position Value:</span>
                  <span className="font-medium">{result.positionValue.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Formula Explanation */}
            <div className="bg-muted/50 rounded p-3 text-sm">
              <div className="font-medium mb-2">Calculation:</div>
              <div className="space-y-1 text-muted-foreground">
                <div>cTrader Lots = TV Position × Ratio</div>
                <div>cTrader Lots = {result.tvPosition} × {result.ratio} = {result.cTraderLots.toFixed(6)}</div>
                <div className="mt-2">Position Value = cTrader Lots × Contract Size</div>
                <div>Position Value = {result.cTraderLots.toFixed(6)} × {result.contractSize.toLocaleString()} = {result.positionValue.toLocaleString()}</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}